import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  getAgentSessionsBoardChannelName,
  getPrivatePusherChannelName,
} from 'redux/selectors/pusher';
import { usePusherSubscription } from 'utils/pusher';
import * as z from 'zod';
import type { AgentPeriod } from './agentTracking.model';

/**
 * Sent by Rails after an upload, a settled prediction, an assignment, a publication
 * or a deletion (TT-6308): on the owner's private channel for their own pages, and on
 * the workspace channel for the live board (TT-6334). Ids and days only; the page
 * reads the sessions back through the normal authorized GET.
 */
export const AGENT_SESSIONS_CHANGED_EVENT = 'agent_sessions_changed';

export const agentSessionsChangedSchema = z.object({
  /** The owner of the sessions; absent from events sent before the live board existed. */
  user_id: z.number().optional(),
  session_ids: z.array(z.number()).default([]),
  days: z.array(z.string()).default([]),
  reason: z.string().optional(),
});
export type AgentSessionsChanged = z.infer<typeof agentSessionsChangedSchema>;
const changedPayloadSchema = agentSessionsChangedSchema;

/** Uploads come in bursts: one read covers the events of this window. */
const DEBOUNCE_MS = 1000;
/** While a popover or menu is open, rows must not reorder under it: try again after this. */
const BUSY_RETRY_MS = 2000;
/** Coming back to the tab reads again only when the last read is older than this. */
const VISIBLE_REFRESH_AFTER_MS = 10_000;

/**
 * A row is being worked with when a popover, menu or listbox is open on the
 * page; a refresh could re-sort the table under it. Tooltips do not count.
 */
export function isEditingRows(doc: Document = document) {
  return !!doc.querySelector(
    ['dialog', 'menu', 'listbox']
      .map(role => `[data-radix-popper-content-wrapper] [role="${role}"]`)
      .join(', ')
  );
}

/**
 * Keeps the viewed window current while the page is open: refreshes once,
 * debounced, when a change event touches a day in the window or a loaded
 * session, and when the tab becomes visible again. A hidden tab reads nothing;
 * an event that arrives while it is hidden is caught up on return. Returns the
 * event handler; `useAgentSessionsChangedEvent` feeds it from Pusher.
 */
export function useLiveRefresh({
  from,
  to,
  periods,
  refresh,
  enabled = true,
  isBusy = isEditingRows,
  isHidden = () => document.hidden,
}: {
  from: string;
  to: string;
  periods: AgentPeriod[];
  refresh: () => Promise<boolean>;
  enabled?: boolean;
  isBusy?: () => boolean;
  isHidden?: () => boolean;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const missedWhileHidden = useRef(false);
  const lastRead = useRef(Date.now());
  const latest = useRef({ from, to, periods, refresh, enabled, isBusy, isHidden });
  latest.current = { from, to, periods, refresh, enabled, isBusy, isHidden };

  const schedule = useCallback((delay: number = DEBOUNCE_MS) => {
    if (timer.current) return;
    timer.current = setTimeout(async () => {
      timer.current = null;
      const current = latest.current;
      // Still loading (or failed): try again, as for a refresh that could not run yet.
      if (!current.enabled) {
        schedule(BUSY_RETRY_MS);
        return;
      }
      if (current.isHidden()) {
        missedWhileHidden.current = true;
        return;
      }
      if (current.isBusy()) {
        schedule(BUSY_RETRY_MS);
        return;
      }
      try {
        const ran = await current.refresh();
        if (ran) lastRead.current = Date.now();
        else schedule(BUSY_RETRY_MS);
      } catch {
        // Keep what is shown; the next event or return to the tab reads again.
      }
    }, delay);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  useEffect(() => {
    const onVisibility = () => {
      if (latest.current.isHidden()) return;
      const stale = Date.now() - lastRead.current > VISIBLE_REFRESH_AFTER_MS;
      if (missedWhileHidden.current || stale) {
        missedWhileHidden.current = false;
        schedule(0);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [schedule]);

  return useCallback(
    (message: unknown) => {
      const parsed = changedPayloadSchema.safeParse(message);
      if (!parsed.success) return;
      const { from: start, to: end, periods: loaded } = latest.current;
      const touchesWindow = parsed.data.days.some(day => day >= start && day <= end);
      const sessionIds = new Set(loaded.map(period => period.sessionId));
      if (touchesWindow || parsed.data.session_ids.some(id => sessionIds.has(id))) schedule();
    },
    [schedule]
  );
}

/** Feeds the signed-in user's agent session change events to `onChange`. */
export function useAgentSessionsChangedEvent(onChange: (message: unknown) => void) {
  const channel = useSelector(getPrivatePusherChannelName);
  usePusherSubscription(channel, AGENT_SESSIONS_CHANGED_EVENT, onChange);
}

/**
 * Feeds every member's agent session change events in this workspace to `onChange`,
 * from the board's own channel, which Rails sends to only while a page holds it open
 * (TT-6334): the live board, or a colleague's sessions (ColleagueAgentSessionsChanges).
 * Holding it open is what makes Rails send every upload's event, so nothing else should.
 */
export function useWorkspaceAgentSessionsChangedEvent(onChange: (message: unknown) => void) {
  const channel = useSelector(getAgentSessionsBoardChannelName);
  usePusherSubscription(channel, AGENT_SESSIONS_CHANGED_EVENT, onChange);
}

/**
 * Feeds one colleague's agent session change events to `onChange` while their sessions
 * are open (TT-6346), from the workspace channel: theirs never reach the viewer's own.
 * Rails lets only admins and team leads hold that channel, so mount this for them only;
 * for anyone else a colleague's page catches up when the tab comes back into view.
 */
export function ColleagueAgentSessionsChanges({
  userId,
  onChange,
}: {
  userId: number;
  onChange: (message: unknown) => void;
}) {
  useWorkspaceAgentSessionsChangedEvent(
    useMemo(() => eventsFor(userId, onChange), [userId, onChange])
  );
  return null;
}

/** Passes on only the change events about one owner's sessions. */
export function eventsFor(userId: number, onChange: (message: unknown) => void) {
  return (message: unknown) => {
    const parsed = changedPayloadSchema.safeParse(message);
    if (parsed.success && parsed.data.user_id === userId) onChange(message);
  };
}
